import { Grid, TableRow, Typography } from "@mui/material";
import { Fragment } from "react/jsx-runtime";
import {
  ImpactCell,
  PublishedCell,
  RiskFactorsCell,
  TitleCell,
} from "~/components/Cells";
import { GenericTable } from "~/components/GenericTable/GenericTable";
import { spacing } from "~/pages/reports/utils/reportsConstants";
import { useAssetReportPolicies } from "../hooks/useAssetReportPolicies";
import { AssetPolicy, Score, ScoreRating } from "~/operations";
import { ReportsNoItemsSection } from "../../components";
import { useAssetReport } from "../hooks/useAssetReport";
import { Asset } from "../utils/assetReportTypes";
import { ReportsSectionHeading } from "~/pages/reports/components/ReportsSectionHeading";

export function AssetReportPolicies() {
  const { asset, policies } = useAssetReport();

  return (
    <Fragment>
      <ReportsSectionHeading heading="Policies" disableUpperPadding />
      {asset &&
        policies.map((policy) => {
          return <PolicyChecksGroup key={policy.mrn} {...{ asset, policy }} />;
        })}
    </Fragment>
  );
}

type PolicyChecksGroupPolicyScore = Pick<Score, "riskRating" | "riskValue">;

type PolicyChecksGroupPolicy = Pick<
  AssetPolicy,
  "mrn" | "name" | "scoreStats"
> & { score: PolicyChecksGroupPolicyScore };

export interface PolicyChecksGroupProps {
  asset: Asset;
  policy: PolicyChecksGroupPolicy;
}

function PolicyChecksGroup({ asset, policy }: PolicyChecksGroupProps) {
  const { passingChecks, failingChecks } = useAssetReportPolicies({
    asset,
    policyMrn: policy.mrn,
  });
  const hasPassingChecks = Boolean(passingChecks.length > 0);
  const hasFailingChecks = Boolean(failingChecks.length > 0);

  return (
    <Grid container sx={{ alignItems: "flex-start", columnGap: 2, mb: 3 }}>
      <Grid
        size="grow"
        sx={{
          display: "flex",
          flexDirection: "column",
          pr: 2,
        }}
      >
        <Typography fontWeight={700}>{policy.name}</Typography>
        {/* TODO: ADD DESCRIPTION TO API */}
        {/* <Typography variant="body2" color="text.secondary">
          {policy.node.description}
        </Typography> */}
      </Grid>
      {/* Cannot Yet do or doesn't make sense */}
      {/* <Grid sx={{ mr: 3 }}>
        <AssetResultsChart blastRadius={fakeData.blastRadius} />
      </Grid> */}
      {/* <Grid>
        <RiskBoxSmall riskLevel={"critical"} riskValue={4} />
      </Grid> */}

      <Grid size={12} sx={{ my: 3 }}>
        <Typography fontWeight={700} sx={{ mb: spacing[25] }}>
          Failing checks
        </Typography>
        {!hasFailingChecks ? (
          <ReportsNoItemsSection target="failing checks" />
        ) : (
          <GenericTable
            items={0}
            tableName="ASSET_REPORT_CHECKS"
            validSortFields={[]}
            printView
          >
            {failingChecks.map((item) => {
              return (
                <TableRow key={item?.mrn}>
                  <ImpactCell
                    impact={item?.riskValue || 0}
                    rating={item?.rating || ScoreRating.Critical}
                    isActive
                  />
                  <TitleCell
                    title={item?.title || ""}
                    tags={item?.tags}
                    options={{ fontSize: 14 }}
                  />
                  <RiskFactorsCell riskFactors={item?.riskFactors} printView />
                  <PublishedCell
                    date={item?.lastUpdated || "Never"}
                    formatting="absolute"
                  />
                </TableRow>
              );
            })}
          </GenericTable>
        )}
      </Grid>
      <Grid size={12} sx={{ mt: 3 }}>
        <Typography fontWeight={700} sx={{ mb: spacing[25] }}>
          Passing checks
        </Typography>
        {!hasPassingChecks ? (
          <ReportsNoItemsSection target="passing checks" />
        ) : (
          <GenericTable
            space={undefined}
            items={0}
            tableName="ASSET_REPORT_CHECKS"
            validSortFields={[]}
            printView
          >
            {passingChecks.map((item) => {
              return (
                <TableRow key={item?.mrn}>
                  <ImpactCell
                    impact={item?.riskValue || 0}
                    rating={item?.rating || ScoreRating.Critical}
                    isActive
                  />
                  <TitleCell
                    title={item?.title || ""}
                    tags={item?.tags}
                    options={{ fontSize: 14 }}
                  />
                  <RiskFactorsCell riskFactors={item?.riskFactors} printView />
                  <PublishedCell date={item?.lastUpdated || "Never"} />
                </TableRow>
              );
            })}
          </GenericTable>
        )}
      </Grid>
    </Grid>
  );
}
